import type { RegistryFormPayload } from '@/types/auth'
import type { HTTPError, DefaultError } from '@/types/httpError'
import { registryApiCall } from '@/api/auth'
import CodeComponent from '@/features/CodeComponent/CodeComponent.vue'
import { useApiCall } from '@/composables/useApiCall'
import {
  EMAIL_REG_EXP,
  PHONE_EXCESSIVE_CHARS_REG_EXP,
  PHONE_REG_EXP,
} from '@/constants/regExp'
import { VALIDATION_MESSAGES } from '@/constants/validationMessages'
import { useAuthStore } from '@/store/auth/auth'
import { computed, defineComponent, reactive, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'RegistryForm',
  components: {
    CodeComponent,
  },
  setup() {
    const fields = reactive({
      name: '',
      email: '',
      password: '',
      repeatPassword: '',
      phone: '',
    })
    const code = ref<string>('')
    const timer = ref<number>(0)
    const timerTimeoutInstance = ref<NodeJS.Timeout | null>(null)
    const arePasswordsShown = reactive({
      password: false,
      repeatPassword: false,
    })
    const isCodeStep = ref(false)
    const isPhoneChangeClicked = ref(false)
    const errorFields = ref<Partial<DefaultError> | null>(null)
    const reqError = ref<string>('')
    const isCodeLoading = ref(false)
    const formRef = ref<HTMLFormElement | null>(null)
    let oldPhone: string | null = null

    const isAutofilled = ref(false)
    const inputElements = computed(() => {
      const emailInput = formRef.value?.querySelector('input[name="email"]')
      const passwordInput = formRef.value?.querySelector('input[name="password"]')
      return [emailInput, passwordInput]
    })

    const router = useRouter()
    const authStore = useAuthStore()

    watch(() => isAutofilled.value, (val) => {
      if (val) {
        inputElements.value.forEach(el => {
          if (el) {
            el.dispatchEvent(new Event('focus', { bubbles: true }))
          }
        })
      }
    })

    watch(timer, (newTime) => {
      if (newTime > 0) {
        if (timerTimeoutInstance.value !== null) {
          clearTimeout(timerTimeoutInstance.value)
        }
        timerTimeoutInstance.value = setTimeout(() => timer.value--, 1000)
      }
    })

    const FIELDS_PLACEHOLDERS = {
      name: 'ФИО',
      email: 'Контактный E-mail',
      password: 'Пароль',
      repeatPassword: 'Повторите пароль',
      phone: 'Телефон',
    }

    const fieldsTypes = computed(() => ({
      name: 'text',
      email: 'email',
      password: arePasswordsShown.password ? 'text' : 'password',
      repeatPassword: arePasswordsShown.repeatPassword ? 'text' : 'password',
      phone: 'tel',
    }))

    const {
      isLoading: registryLoading,
      // data: registryAdminData,
      executeApiCall: registryAdminAction,
      error: registryAdminError,
    } = useApiCall<'', DefaultError, RegistryFormPayload>(
      registryApiCall,
      true,
    )

    const isLoading = computed(
      () => registryLoading.value || isCodeLoading.value,
    )

    const showPassword = (key: keyof typeof arePasswordsShown) => {
      arePasswordsShown[key] = !arePasswordsShown[key]
    }

    // валидация
    const isNameValid = computed(() => !EMAIL_REG_EXP.test(fields.name))
    const isPasswordValid = computed(() =>
      fields.password.length > 7
      && fields.password.match(/^(?=.*[\W_])/)
      && fields.password.match(/^(?=.*\d)/)
      && fields.password.match(/^(?=.*[a-z])/i),
    )
    const isRepeatPasswordValid = computed(
      () => fields.password === fields.repeatPassword,
    )
    const isEmailValid = computed(() =>
      fields.email ? EMAIL_REG_EXP.test(fields.email) : true,
    )
    const isPhoneValid = computed(() =>
      fields.phone ? PHONE_REG_EXP.test(fields.phone) : true,
    )

    watch(
      [isPasswordValid, isRepeatPasswordValid],
      ([isPassValidValue, isRepeatPassValidValue]) => {
        delete errorFields.value?.data?.password
        delete errorFields.value?.data?.repeatPassword
        if (!isPassValidValue) {
          errorFields.value = {
            data: {
              ...errorFields.value?.data,
              password: [VALIDATION_MESSAGES.password.validationErr],
            },
          }
        }
        if (!isRepeatPassValidValue) {
          errorFields.value = {
            data: {
              ...errorFields.value?.data,
              repeatPassword: [
                VALIDATION_MESSAGES.repeatPassword.notSameAsPasswordErr,
              ],
            },
          }
        }
      },
    )

    watch(() => isNameValid.value, (newVal) => {
      delete errorFields.value?.data?.name
      if (!newVal) {
        errorFields.value = {
          data: {
            ...errorFields.value?.data,
            name: ['ФИО не может содержать адрес электронной почты'],
          },
        }
      }
    }, { immediate: true })

    watch(
      () => isEmailValid.value,
      (newVal) => {
        delete errorFields.value?.data?.email
        if (!newVal) {
          errorFields.value = {
            data: {
              ...errorFields.value?.data,
              email: [VALIDATION_MESSAGES.email.validationErr],
            },
          }
        }
      },
      { immediate: true },
    )

    watch(
      () => isPhoneValid.value,
      (newVal) => {
        delete errorFields.value?.data?.phone

        if (!newVal) {
          errorFields.value = {
            data: {
              ...errorFields.value?.data,
              phone: [VALIDATION_MESSAGES.phone.validationErr],
            },
          }
        }
      },
    )

    const isSubmitBtnDisabled = computed(() =>
      !isCodeStep.value
        ? !Object.values(fields).every((field) => field.trim().length)
        || fields.phone.length !== 16
        || !isPasswordValid.value
        || !isRepeatPasswordValid.value
        : code.value.length < 5,
    )

    const onChangePhoneClick = () => {
      isCodeStep.value = false
      isPhoneChangeClicked.value = true
      oldPhone = fields.phone
    }

    const resetErrors = () => {
      errorFields.value = null
      reqError.value = ''
    }

    const handleRegistryRequest = async () => {
      const body = {
        ...fields,
        phone: fields.phone.replace(PHONE_EXCESSIVE_CHARS_REG_EXP, ''),
      }

      try {
        if (isPhoneChangeClicked.value) {
          await authStore.changeRegistryPhone({
            oldPhone: oldPhone as string,
            phone: body.phone,
          })
        } else {
          await registryAdminAction(body)
        }
      } catch (error) {
        if (error || registryAdminError.value) {
          errorFields.value = error as DefaultError || registryAdminError.value
        }
        if (!(error as DefaultError)?.data && (error as DefaultError)?.error) {
          reqError.value = (error as DefaultError).error
        }
        if (error) {
          throw (error as DefaultError).data
        }
        if (registryAdminError.value) {
          throw registryAdminError.value?.data.data
        }
      }
    }
    const isCodeSend = ref(false)
    const sendCodeTimer = () => {
      isCodeSend.value = true
      setTimeout(() => {
        isCodeSend.value = false
      }, 5000)
    }
    const handleRegistryFormSubmit = async () => {
      resetErrors()

      try {
        await handleRegistryRequest()

        arePasswordsShown.password = false
        arePasswordsShown.repeatPassword = false
        if (timerTimeoutInstance.value !== null) {
          clearTimeout(timerTimeoutInstance.value)
        }
        timer.value = 59
        isCodeStep.value = true
        sendCodeTimer()
      } catch (error) {
        // if (error && !error.data) {
        //   reqError.value = (error as HTTPError<DefaultError>).data.error
        // }
        if (!isPhoneChangeClicked.value) {
          if (!(error as DefaultError)?.data && (error as DefaultError)?.error) {
            reqError.value = (error as DefaultError).error
          }
          errorFields.value = error as DefaultError['data']
        }
      }
    }

    const handleSendSMS = async () => {
      resetErrors()
      isCodeLoading.value = true

      const body = {
        phone: fields.phone.replace(PHONE_EXCESSIVE_CHARS_REG_EXP, '').slice(1),
      }

      try {
        await authStore.sendSMS(body)
        timer.value = 59
        sendCodeTimer()
      } catch (error) {
        reqError.value = (error as HTTPError<DefaultError>).data.error
      } finally {
        isCodeLoading.value = false
      }
    }

    const handleCompleteSubmit = async () => {
      resetErrors()
      isCodeLoading.value = true

      const body = {
        phone: fields.phone.replace(PHONE_EXCESSIVE_CHARS_REG_EXP, '').slice(1),
        code: code.value,
      }

      try {
        await authStore.completeLogin(body)
        if (authStore.isAuth) {
          await router.push({ name: 'startScreen' })
        }
      } catch (error) {
        reqError.value = (error as HTTPError<DefaultError>).data.error
      } finally {
        isCodeLoading.value = false
      }
    }

    const submitHandler = computed(() =>
      isCodeStep.value ? handleCompleteSubmit : handleRegistryFormSubmit,
    )

    const onAutofill = () => isAutofilled.value = true

    const checkIfBrowserChromeBased = () => {
      const ua = navigator.userAgent
      return /Chrome/.test(ua) && !/Edg|OPR/.test(ua)
    }

    const checkAutofill = () => {
      inputElements.value.forEach((input) => {
        if (input) {
          input.addEventListener('animationstart', onAutofill)
        }
      })
    }

    onMounted(() => {
      if (checkIfBrowserChromeBased()) {
        checkAutofill()
      }
    })

    onUnmounted(() => {
      inputElements.value.forEach((input) => {
        if (input) {
          input.removeEventListener('animationstart', onAutofill)
        }
      })
    })
    return {
      fields,
      code,
      timer,
      errorFields,
      reqError,
      arePasswordsShown,
      isCodeStep,
      isPhoneChangeClicked,
      formRef,
      FIELDS_PLACEHOLDERS,
      PHONE_EXCESSIVE_CHARS_REG_EXP,
      fieldsTypes,
      showPassword,
      isLoading,
      isEmailValid,
      isPhoneValid,
      VALIDATION_MESSAGES,
      isSubmitBtnDisabled,
      submitHandler,
      handleSendSMS,
      onChangePhoneClick,
      isCodeSend,
    }
  },
})
